import { getCargoOperationWeight } from 'src/utils/stowage/cargoOperationUtils';
import { calculateVolume } from 'src/utils/stowage/stowageOperation/volumeUtils';
import Decimal from 'decimal.js';
import { CargoOperationVM } from '../types/CargoOperationVM';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getCargoTemperature, getDefaultCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { optionsConfiguration } from 'src/utils/stowage/cargoOptionUtils';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';
import { isNullOrUndefined } from 'src/utils/objectUtils';

const getValueWithFallback = <T>(
    productChanges: Product | null | undefined,
    product: Product | null | undefined,
    propertyPath: string,
    defaultValue: T
): T => {
    const properties = propertyPath.split('.');
    const getNestedValue = (obj: any): T | undefined => (obj ? (properties.reduce((acc, part) => acc?.[part], obj) as T) : undefined);

    return getNestedValue(productChanges) ?? getNestedValue(product) ?? defaultValue;
};

const getTemperatureRange = (cargoOperationVM: CargoOperationVM, rangeName: 'loading' | 'voyage' | 'unloading', type: 'min' | 'max'): number => {
    const propertyPath = `${rangeName}TemperatureRange.${type}`;
    return getValueWithFallback(cargoOperationVM.cargo.productChanges, cargoOperationVM.cargo.product, propertyPath, 0);
};

export const getCorrectedLitreWeightValue = (cargoOperationVM: CargoOperationVM): number => {
    if (!cargoOperationVM.cargo.product) return 0;

    const adjustedTemperature = cargoOperationVM.cargo.temperature - getLitreWeightReferenceTemperature(cargoOperationVM);
    const correction = getLitreWeightCorrectionFactor(cargoOperationVM) * adjustedTemperature;

    return getLitreWeight(cargoOperationVM) - correction;
};

export const isCorrectedLitreWeightAdjusted = (cargoOperationVM: CargoOperationVM): boolean => {
    if (!cargoOperationVM.cargo.productChanges) return false;

    if (
        ((!isNullOrUndefined(cargoOperationVM.cargo.productChanges?.litreWeightReferenceTemperature) && cargoOperationVM.cargo.productChanges?.litreWeightReferenceTemperature !== cargoOperationVM.cargo.product?.litreWeightReferenceTemperature)) ||
        ((!isNullOrUndefined(cargoOperationVM.cargo.productChanges?.litreWeightCorrectionFactor) && cargoOperationVM.cargo.productChanges?.litreWeightCorrectionFactor !== cargoOperationVM.cargo.product?.litreWeightCorrectionFactor)) ||
        ((!isNullOrUndefined(cargoOperationVM.cargo.productChanges?.litreWeight) && cargoOperationVM.cargo.productChanges?.litreWeight !== cargoOperationVM.cargo.product?.litreWeight)) ||
        isCargoTemperatureAdjusted(cargoOperationVM)
    ) {
        return true;
    }

};

export const isCargoTemperatureAdjusted = (cargoOperationVM: CargoOperationVM): boolean => {
    if (!cargoOperationVM.cargo.product) return false;

    if (cargoOperationVM.cargo.temperature !== getDefaultCargoTemperature(cargoOperationVM.cargo)) {
        return true;
    }

    return false;
};

export const getLitreWeightReferenceTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getValueWithFallback(
        cargoOperationVM.cargo.productChanges,
        cargoOperationVM.cargo.product,
        'litreWeightReferenceTemperature',
        cargoOperationVM.cargo.product?.litreWeightReferenceTemperature ?? 0
    );
};

export const getLitreWeight = (cargoOperationVM: CargoOperationVM): number => {
    return getValueWithFallback(
        cargoOperationVM.cargo.productChanges,
        cargoOperationVM.cargo.product,
        'litreWeight',
        cargoOperationVM.cargo.product?.litreWeight ?? 0
    );
};

export const getLitreWeightCorrectionFactor = (cargoOperationVM: CargoOperationVM): number => {
    return getValueWithFallback(
        cargoOperationVM.cargo.productChanges,
        cargoOperationVM.cargo.product,
        'litreWeightCorrectionFactor',
        cargoOperationVM.cargo.product?.litreWeightCorrectionFactor ?? 0
    );
};

export const getTradeName = (cargoOperationVM: CargoOperationVM): string | null => {
    return getValueWithFallback(cargoOperationVM.cargo.productChanges, cargoOperationVM.cargo.product, 'tradeName', null);
};

export const getCharterName = (cargoOperationVM: CargoOperationVM): string | null => {
    return getValueWithFallback(cargoOperationVM.cargo.productChanges, cargoOperationVM.cargo.product, 'chartererName', null);
};

export const getCargoTemperatureValue = (cargoOperationVM: CargoOperationVM): number => {
    if (cargoOperationVM.cargo.temperature) {
        return cargoOperationVM.cargo.temperature;
    }
    return getValueWithFallback(
        cargoOperationVM.cargo.productChanges,
        cargoOperationVM.cargo.product,
        'voyageTemperatureRange.max',
        cargoOperationVM.cargo.product?.voyageTemperatureRange.max ?? 0
    );
};

export const getMaxAdjacentTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getValueWithFallback(
        cargoOperationVM.cargo.productChanges,
        cargoOperationVM.cargo.product,
        'maxAdjacentTemperature',
        cargoOperationVM.cargo.product?.maxAdjacentTemperature ?? 0
    );
};

export const getLoadingMinTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getTemperatureRange(cargoOperationVM, 'loading', 'min');
};

export const getLoadingMaxTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getTemperatureRange(cargoOperationVM, 'loading', 'max');
};

export const getVoyageMinTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getTemperatureRange(cargoOperationVM, 'voyage', 'min');
};

export const getVoyageMaxTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getTemperatureRange(cargoOperationVM, 'voyage', 'max');
};

export const getUnloadingMinTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getTemperatureRange(cargoOperationVM, 'unloading', 'min');
};

export const getUnloadingMaxTemperature = (cargoOperationVM: CargoOperationVM): number => {
    return getTemperatureRange(cargoOperationVM, 'unloading', 'max');
};

export const getTotalContractWeight = (cargoOperationVM: CargoOperationVM, stowagePlan: StowagePlan): number => {
    if (!cargoOperationVM?.cargo.cargoGuid || !cargoOperationVM.operationType) return 0;

    const stowedContractWeight = new Decimal(getCargoOperationWeight(stowagePlan, cargoOperationVM.cargo, 'Loading', 'contract'));
    const contractWeight = new Decimal(cargoOperationVM.contractWeight ?? 0);
    if (stowedContractWeight.gte(contractWeight)) {
        const originalContractWeight = new Decimal(cargoOperationVM.originalContractWeight ?? 0);
        return stowedContractWeight.minus(originalContractWeight).plus(contractWeight).toNumber();
    }
    return contractWeight.toNumber();
};

export const getTotalNominatedWeight = (cargoOperationVM: CargoOperationVM, stowagePlan: StowagePlan): number => {
    if (!cargoOperationVM?.cargo.cargoGuid || !cargoOperationVM.operationType) return 0;

    const stowedNominatedWeight = new Decimal(getCargoOperationWeight(stowagePlan, cargoOperationVM.cargo, 'Loading', 'nominated'));
    const nominatedWeight = new Decimal(cargoOperationVM.nominatedWeight ?? 0);
    if (stowedNominatedWeight.gte(nominatedWeight)) {
        const originalNominatedWeight = new Decimal(cargoOperationVM.originalNominatedWeight ?? 0);
        return stowedNominatedWeight.minus(originalNominatedWeight).plus(nominatedWeight).toNumber();
    }
    return nominatedWeight.toNumber();
};

export const getTotalNominatedVolume = (cargoOperationVM: CargoOperationVM, stowagePlan: StowagePlan): number => {
    if (!cargoOperationVM.cargo.product) return 0;

    const totalNominatedWeight = getTotalNominatedWeight(cargoOperationVM, stowagePlan);
    return calculateVolume(
        new Decimal(totalNominatedWeight),
        cargoOperationVM.cargo,
        getCargoTemperature(stowagePlan, cargoOperationVM.cargo)
    ).toNumber();
};

export const getOptionsList = () => {
    return optionsConfiguration
        .filter((option) => typeof option.optionType === 'string' && option.optionType.trim() !== '')
        .map((option) => ({
            value: option.optionType,
            label: option.optionType,
        }));
};

export const getOptionMinusValue = (cargoOperationVM: CargoOperationVM): number => {
    return Math.abs(cargoOperationVM.cargo.optionMinusPercentage ?? 0);
};

export const getOptionPlusValue = (cargoOperationVM: CargoOperationVM): number => {
    return Math.abs(cargoOperationVM.cargo.optionPlusPercentage ?? 0);
};
